import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

import { TOKEN_EXPIRY_WARN_MS } from '../app.constants';

export type SessionWarningType = 'token_expiry' | 'idle';

const STORAGE_KEYS = {
  WARNING: 'omni_session_warning',
  EXTENDED: 'omni_session_extended',
  IDLE_RESET: 'omni_session_idle_reset',
  IDLE_LOGOUT: 'omni_session_idle_logout',
  ACTIVITY: 'omni_session_activity',
};

/**
 * How long tabs wait after claiming a warning before deciding who owns it.
 * Because every tab reaches the idle / token-expiry threshold at almost the
 * same instant, a synchronous "first to write wins" claim races (all tabs read
 * an empty slot, all write, all think they won). Instead each tab publishes its
 * claim, waits this short window to collect competing claims, then a single
 * deterministic winner (earliest timestamp, lowest tab id) shows the modal.
 */
const CLAIM_DEBOUNCE_MS = 250;

/** Throttle for broadcasting local user activity to other tabs. */
const ACTIVITY_THROTTLE_MS = 10 * 1000;

interface SessionWarningPayload {
  type: SessionWarningType;
  at: number;
  tabId: string;
}

interface ClaimInfo {
  at: number;
  tabId: string;
}

@Injectable({ providedIn: 'root' })
export class SessionTabSyncService implements OnDestroy {
  /** Dismiss timeout / expiry modals in this tab. */
  readonly dismissModals$ = new Subject<void>();

  /** Another tab extended the JWT session. */
  readonly sessionExtended$ = new Subject<void>();

  /** Another tab dismissed the idle warning and reset the idle timer. */
  readonly idleReset$ = new Subject<void>();

  /** Another tab's idle warning timed out and logged the user out. */
  readonly idleLogout$ = new Subject<void>();

  /** Another tab claimed the warning; do not open a duplicate modal here. */
  readonly warningClaimedElsewhere$ = new Subject<SessionWarningType>();

  /** The user did something in another tab; treat this tab as active too. */
  readonly activityElsewhere$ = new Subject<void>();

  private readonly tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  private readonly activeElections = new Map<SessionWarningType, ClaimInfo[]>();
  private lastActivityBroadcast = 0;

  private readonly onStorage = (event: StorageEvent) => {
    if (!event.key) {
      return;
    }
    this.ngZone.run(() => {
      if (event.key === STORAGE_KEYS.WARNING) {
        if (event.newValue) {
          const payload = this.parseWarning(event.newValue);
          if (payload && payload.tabId !== this.tabId) {
            const election = this.activeElections.get(payload.type);
            if (election) {
              election.push({ at: payload.at, tabId: payload.tabId });
            }
            this.warningClaimedElsewhere$.next(payload.type);
          }
        }
        return;
      }
      if (event.key === STORAGE_KEYS.EXTENDED && event.newValue) {
        this.clearWarning();
        this.sessionExtended$.next();
        this.dismissModals$.next();
        return;
      }
      if (event.key === STORAGE_KEYS.IDLE_RESET && event.newValue) {
        this.clearWarning();
        this.idleReset$.next();
        this.dismissModals$.next();
        return;
      }
      if (event.key === STORAGE_KEYS.IDLE_LOGOUT && event.newValue) {
        this.clearWarning();
        this.idleLogout$.next();
        this.dismissModals$.next();
        return;
      }
      if (event.key === STORAGE_KEYS.ACTIVITY && event.newValue) {
        this.activityElsewhere$.next();
        return;
      }
      if (event.key === 'currentUser' && !event.newValue) {
        this.clearWarning();
        this.dismissModals$.next();
      }
    });
  };

  constructor(private ngZone: NgZone) {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorage);
    }
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorage);
    }
  }

  /**
   * Claim the right to show a warning modal of the given type and resolve to
   * `true` only in the single tab that should display it. Resolves to `false`
   * in every other tab so they stay silent.
   */
  requestWarning(type: SessionWarningType): Promise<boolean> {
    if (typeof window === 'undefined') {
      return Promise.resolve(true);
    }
    const existing = this.readWarning();
    if (existing && existing.type === type && existing.tabId !== this.tabId && this.isWarningFresh(existing.at)) {
      return Promise.resolve(false);
    }

    const claim: ClaimInfo = { at: Date.now(), tabId: this.tabId };
    const claims: ClaimInfo[] = [claim];
    this.activeElections.set(type, claims);
    this.writeWarning({ type, at: claim.at, tabId: claim.tabId });

    return new Promise<boolean>((resolve) => {
      setTimeout(() => {
        this.activeElections.delete(type);
        const winner = claims.reduce(
          (best, c) => (c.at < best.at || (c.at === best.at && c.tabId < best.tabId) ? c : best),
          claim,
        );
        resolve(winner.tabId === this.tabId);
      }, CLAIM_DEBOUNCE_MS);
    });
  }

  isWarningActive(type?: SessionWarningType): boolean {
    const existing = this.readWarning();
    if (!existing || !this.isWarningFresh(existing.at)) {
      return false;
    }
    return !type || existing.type === type;
  }

  clearWarning() {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem(STORAGE_KEYS.WARNING);
  }

  notifySessionExtended() {
    this.clearWarning();
    if (typeof window === 'undefined') {
      this.sessionExtended$.next();
      this.dismissModalsLocally();
      return;
    }
    localStorage.setItem(STORAGE_KEYS.EXTENDED, String(Date.now()));
    this.sessionExtended$.next();
    this.dismissModalsLocally();
  }

  notifyIdleReset() {
    this.clearWarning();
    if (typeof window === 'undefined') {
      this.idleReset$.next();
      this.dismissModalsLocally();
      return;
    }
    localStorage.setItem(STORAGE_KEYS.IDLE_RESET, String(Date.now()));
    this.idleReset$.next();
    this.dismissModalsLocally();
  }

  notifyIdleLogout() {
    this.clearWarning();
    if (typeof window === 'undefined') {
      this.idleLogout$.next();
      this.dismissModalsLocally();
      return;
    }
    localStorage.setItem(STORAGE_KEYS.IDLE_LOGOUT, String(Date.now()));
    this.idleLogout$.next();
    this.dismissModalsLocally();
  }

  /** Broadcast local user activity to other tabs (throttled). */
  notifyActivity() {
    if (typeof window === 'undefined') {
      return;
    }
    const now = Date.now();
    if (now - this.lastActivityBroadcast < ACTIVITY_THROTTLE_MS) {
      return;
    }
    this.lastActivityBroadcast = now;
    localStorage.setItem(STORAGE_KEYS.ACTIVITY, String(now));
  }

  dismissModalsLocally() {
    this.dismissModals$.next();
  }

  private readWarning(): SessionWarningPayload | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return this.parseWarning(localStorage.getItem(STORAGE_KEYS.WARNING));
  }

  private writeWarning(payload: SessionWarningPayload) {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(STORAGE_KEYS.WARNING, JSON.stringify(payload));
  }

  private parseWarning(raw: string | null): SessionWarningPayload | null {
    if (!raw) {
      return null;
    }
    try {
      const payload = JSON.parse(raw);
      if (!payload || !payload.type || !payload.at) {
        return null;
      }
      return payload as SessionWarningPayload;
    } catch {
      return null;
    }
  }

  private isWarningFresh(at: number): boolean {
    return Date.now() - at < TOKEN_EXPIRY_WARN_MS + 60 * 1000;
  }
}
